import { useCallback, useReducer, useRef } from 'react';
/**
 * A minimal `useMutation` replacement (no React Query). Wraps an async
 * mutation fn with loading/error state and success/error callbacks. Used by
 * collection-backed mutation hooks that previously returned `UseMutationResult`.
 */ export function useMutationFn(options) {
    const optsRef = useRef(options);
    optsRef.current = options;
    const [state, dispatch] = useReducer((prev, action)=>{
        switch(action.type){
            case 'start':
                return {
                    isLoading: true,
                    isError: false,
                    error: null
                };
            case 'success':
                return {
                    isLoading: false,
                    isError: false,
                    error: null
                };
            case 'error':
                return {
                    isLoading: false,
                    isError: true,
                    error: action.error
                };
            case 'reset':
                return {
                    isLoading: false,
                    isError: false,
                    error: null
                };
            default:
                return prev;
        }
    }, {
        isLoading: false,
        isError: false,
        error: null
    });
    const mutateAsync = useCallback(async (args)=>{
        dispatch({
            type: 'start'
        });
        try {
            var _optsRef_current_onSuccess, _optsRef_current;
            const data = await optsRef.current.mutationFn(args);
            await ((_optsRef_current_onSuccess = (_optsRef_current = optsRef.current).onSuccess) === null || _optsRef_current_onSuccess === void 0 ? void 0 : _optsRef_current_onSuccess.call(_optsRef_current, data, args));
            dispatch({
                type: 'success'
            });
            return data;
        } catch (error) {
            var _optsRef_current_onError, _optsRef_current1;
            (_optsRef_current_onError = (_optsRef_current1 = optsRef.current).onError) === null || _optsRef_current_onError === void 0 ? void 0 : _optsRef_current_onError.call(_optsRef_current1, error, args);
            dispatch({
                type: 'error',
                error
            });
            throw error;
        } finally{
            var _optsRef_current_onSettled, _optsRef_current2;
            (_optsRef_current_onSettled = (_optsRef_current2 = optsRef.current).onSettled) === null || _optsRef_current_onSettled === void 0 ? void 0 : _optsRef_current_onSettled.call(_optsRef_current2);
        }
    }, []);
    const mutate = useCallback((args)=>{
        mutateAsync(args).catch(()=>undefined);
    }, [
        mutateAsync
    ]);
    const reset = useCallback(()=>dispatch({
            type: 'reset'
        }), []);
    return {
        mutate,
        mutateAsync,
        isLoading: state.isLoading,
        isError: state.isError,
        error: state.error,
        reset
    };
}
